import React, { useCallback } from 'react'
import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'
import { summarizeRowsLeading } from '@/lib/text/leading'
import type { ShapeTextParagraphsChangeOptions } from '@/components/app/shapes/shape-text-editor/hooks/use-shape-text-editing'
import {
  applyStyleToCharRange,
  applyStyleToExactCharRange,
  getKerningStateAtFlatCaret,
} from '@/lib/shapes/shape-text-edits'
import {
  type ShapeTextRange,
  paragraphRangeAt,
  visualRowRangesOf,
  visualRowsSpanOf,
} from '@/lib/shapes/shape-text-navigation'
import type { ShapeTextCaret } from '@/lib/shapes/types/shape-text-caret'
import { summarizeDropCapSelection } from '@/lib/text/drop-cap'

export type KerningCaretState = { available: boolean; value: number }

interface UseShapeTextStyleOptions {
  charRunMap: Array<TextRun | null>
  paragraphs: Paragraph[]
  shape: ShapeData
  /** The laid-out carets, for the visual rows a Leading change has to cover. */
  carets: ShapeTextCaret[]
  /** Characters the layout drew; text past it has no rows. */
  consumedLength: number
  onParagraphsChange: (
    shapeId: string,
    paragraphs: Paragraph[],
    options?: ShapeTextParagraphsChangeOptions
  ) => void
  textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>
  pendingRunStyleRef: React.MutableRefObject<TextRunStyleUpdate | null>
}

export interface UseShapeTextStyleResult {
  applyRunStyle: (update: TextRunStyleUpdate, options?: ShapeTextParagraphsChangeOptions) => void
  getEditingStyle: () => TextRunStyleUpdate | null
  /** Apply a kerning value to the glyph pair immediately before a collapsed caret. */
  applyKerningAtCaret: (value: number) => boolean
  /** Kerning state (availability + value) for the glyph pair at the current caret. */
  getKerningState: () => KerningCaretState
  /** Records that a keyboard-driven font-size step just got clamped at FONT_SIZE_MIN/MAX — the
   *  next `getEditingStyle()` call reports it via `pointSizeClampedAtBoundary`, then clears it. */
  markPointSizeBoundaryHit: () => void
}

/**
 * The visual row a bare caret is on, as `[start, end)`, or `undefined` when it has none (an empty
 * paragraph, or text past `consumedLength` that the layout never drew). The character at the caret
 * names its row (the row below at a wrap); at the end of a line or of the text there is none, so
 * the one before it does. Leading is read from this row and written to it, so both agree.
 */
function caretRowRange(
  carets: ShapeTextCaret[],
  charRunMap: Array<TextRun | null>,
  caret: number,
  consumedLength: number
): ShapeTextRange | undefined {
  const rowOffset = caret < consumedLength && charRunMap[caret] != null ? caret : caret - 1

  if (rowOffset < 0 || rowOffset >= consumedLength || charRunMap[rowOffset] == null) {
    return undefined
  }

  return visualRowRangesOf(carets, rowOffset, rowOffset + 1, consumedLength)[0]
}

/** Style of the runs under `[start, end)`, with `mixedTracking`/`mixedLeading` set when they differ. */
function selectionMixedStyle(
  charRunMap: Array<TextRun | null>,
  start: number,
  end: number,
  paragraphs: Paragraph[],
  carets: ShapeTextCaret[],
  consumedLength: number
): TextRunStyleUpdate | null {
  let first: TextRun | null = null
  let mixedPointSize = false
  let mixedTracking = false
  let mixedKerning = false
  let mixedFontFamilyId = false
  let mixedFontId = false
  const selectionFontIds = new Set<number>()

  for (let i = start; i < end; i++) {
    const run = charRunMap[i]

    if (!run) continue
    if (typeof run.fontId === 'number') selectionFontIds.add(run.fontId)

    if (!first) {
      first = run
      continue
    }

    if ((run.pointSize ?? DEFAULT_POINT_SIZE) !== (first.pointSize ?? DEFAULT_POINT_SIZE)) {
      mixedPointSize = true
    }

    if ((run.tracking ?? 0) !== (first.tracking ?? 0)) mixedTracking = true
    if ((run.kerning ?? 0) !== (first.kerning ?? 0)) mixedKerning = true
    if ((run.fontFamilyId ?? null) !== (first.fontFamilyId ?? null)) mixedFontFamilyId = true
    if ((run.fontId ?? null) !== (first.fontId ?? null)) mixedFontId = true
  }

  if (!first) return null

  // Leading is a row property, so whole rows are read, unselected text included: the same rows
  // `applyRunStyle` writes it to.
  const rowsLeading = summarizeRowsLeading(
    visualRowRangesOf(carets, start, end, consumedLength).map((row) =>
      charRunMap.slice(row.start, row.end).filter((run): run is TextRun => run != null)
    )
  )
  const mixedLeading = rowsLeading?.mixed ?? false
  const style: TextRunStyleUpdate = {
    isRangeSelection: true,
    mixedPointSize,
    mixedTracking,
    mixedLeading,
    mixedKerning,
    mixedFontFamilyId,
    mixedFontId,
    selectionFontIds: [...selectionFontIds],
  }

  if (!mixedFontFamilyId && typeof first.fontFamilyId === 'number') {
    style.fontFamilyId = first.fontFamilyId
  }

  if (!mixedFontId && typeof first.fontId === 'number') style.fontId = first.fontId
  if (!mixedPointSize && typeof first.pointSize === 'number') style.pointSize = first.pointSize
  if (rowsLeading && !mixedLeading) {
    if (rowsLeading.leading !== undefined) style.leading = rowsLeading.leading
    style.leadingPointSize = rowsLeading.pointSize
  }

  if (!mixedTracking && typeof first.tracking === 'number') style.tracking = first.tracking
  if (typeof first.underline === 'boolean') style.underline = first.underline
  if (typeof first.strikethrough === 'boolean') style.strikethrough = first.strikethrough

  // Alignment: read from the first paragraph the selection touches — unchanged, no mixed-state
  // concept for alignment here. Drop cap is different: a real (non-collapsed) selection can span
  // a paragraph boundary where the two sides genuinely disagree (WMPCM — "Text tool: several
  // paragraphs" from the same user story `summarizeDropCapSelection`'s own whole-frame Select-Tool
  // case already covers), so every paragraph the range TOUCHES is collected — not just the one
  // containing `start` — and resolved through that same function, which is the single source of
  // truth both this and the whole-frame path resolve mixed drop cap through.
  let paraOffset = 0
  let alignmentSet = false
  const touchedParagraphs: Paragraph[] = []

  for (const para of paragraphs) {
    const paraLen = (para.textLines ?? [])
      .flatMap((l) => l.textRuns ?? [])
      .reduce((sum, r) => sum + (r.textContent ?? '').length, 0)
    const paraStart = paraOffset
    const paraEnd = paraOffset + paraLen

    if (!alignmentSet && start <= paraEnd) {
      if (typeof para.alignment === 'number') style.alignment = para.alignment
      alignmentSet = true
    }

    // Half-open-range overlap test against this paragraph's own `[paraStart, paraEnd]` (inclusive
    // upper bound, matching the alignment lookup above and `paragraphIndexAtFlatOffset`'s own
    // convention for "which paragraph does this flat offset belong to").
    if (start <= paraEnd && end > paraStart) touchedParagraphs.push(para)

    paraOffset += paraLen + 1
  }

  style.dropCapSummary = summarizeDropCapSelection(touchedParagraphs)

  return style
}

export function useShapeTextStyle({
  charRunMap,
  paragraphs,
  shape,
  carets,
  consumedLength,
  onParagraphsChange,
  textareaRef,
  pendingRunStyleRef,
}: UseShapeTextStyleOptions): UseShapeTextStyleResult {
  const applyRunStyle = useCallback(
    (update: TextRunStyleUpdate, options?: ShapeTextParagraphsChangeOptions) => {
      const ta = textareaRef.current

      if (!ta) return
      const selStart = ta.selectionStart ?? 0
      const selEnd = ta.selectionEnd ?? selStart

      if (selStart !== selEnd) {
        const { leading, ...runUpdate } = update
        let updatedParagraphs =
          Object.keys(runUpdate).length > 0
            ? applyStyleToCharRange(paragraphs, runUpdate, selStart, selEnd)
            : paragraphs

        // Leading is line spacing, so it reaches every visual row the selection touches, not just
        // the selected characters. Selecting one word on a row left the rest of the row on its old
        // value, and the row kept spacing by that (WMPCM-12951).
        if (leading !== undefined) {
          const rows = visualRowsSpanOf(carets, selStart, selEnd, consumedLength)

          updatedParagraphs = applyStyleToCharRange(
            updatedParagraphs,
            { leading },
            rows.start,
            rows.end
          )
        }

        if (options) {
          onParagraphsChange(shape.id, updatedParagraphs, options)
        } else {
          onParagraphsChange(shape.id, updatedParagraphs)
        }

        pendingRunStyleRef.current = null
      } else {
        // Alignment and drop cap are paragraph-level: apply immediately to the caret's paragraph.
        if (
          update.alignment !== undefined ||
          update.dropCap !== undefined ||
          update.dropCapLines !== undefined ||
          update.dropCapOffset !== undefined
        ) {
          let paraOffset = 0
          const updated = paragraphs.map((para) => {
            const paraLen = (para.textLines ?? [])
              .flatMap((l) => l.textRuns ?? [])
              .reduce((sum, r) => sum + (r.textContent ?? '').length, 0)
            const inThisPara = selStart >= paraOffset && selStart <= paraOffset + paraLen

            paraOffset += paraLen + 1

            if (!inThisPara) return para

            return {
              ...para,
              ...(update.alignment !== undefined ? { alignment: update.alignment } : {}),
              ...(update.dropCap !== undefined ? { dropCap: update.dropCap } : {}),
              ...(update.dropCapLines !== undefined ? { dropCapLines: update.dropCapLines } : {}),
              ...(update.dropCapOffset !== undefined
                ? { dropCapOffset: update.dropCapOffset }
                : {}),
            }
          })

          onParagraphsChange(shape.id, updated)
        }

        // Leading is line spacing, so at a bare caret it applies right away to the row the caret is
        // on — the row the panel reads it from — instead of waiting as a pending style for the next
        // typed character. Pending left the panel showing a value the text never received
        // (WMPCM-9899), and the whole paragraph was more than the AC asks for (WMPCM-12951). Text
        // past the layout has no rows, so its paragraph takes it. An empty paragraph has no
        // characters to style; it stays pending.
        let leadingApplied = false

        if (update.leading !== undefined) {
          const { start, end } =
            caretRowRange(carets, charRunMap, selStart, consumedLength) ??
            paragraphRangeAt(ta.value, selStart)

          if (end > start) {
            const updated = applyStyleToCharRange(
              paragraphs,
              { leading: update.leading },
              start,
              end
            )

            if (options) {
              onParagraphsChange(shape.id, updated, options)
            } else {
              onParagraphsChange(shape.id, updated)
            }

            leadingApplied = true
          }
        }

        // Run-level properties → pending (applied to next typed characters).
        const {
          alignment: _a,
          dropCap: _dc,
          dropCapLines: _dcl,
          dropCapOffset: _dco,
          ...pendingUpdate
        } = update
        const { leading: _l, ...withoutLeading } = pendingUpdate
        const runUpdate = leadingApplied ? withoutLeading : pendingUpdate

        if (Object.keys(runUpdate).length > 0) {
          pendingRunStyleRef.current = { ...(pendingRunStyleRef.current ?? {}), ...runUpdate }
        }
      }
    },
    [
      carets,
      charRunMap,
      consumedLength,
      onParagraphsChange,
      paragraphs,
      pendingRunStyleRef,
      shape.id,
      textareaRef,
    ]
  )

  const pointSizeBoundaryHitRef = React.useRef(false)

  const markPointSizeBoundaryHit = useCallback(() => {
    pointSizeBoundaryHitRef.current = true
  }, [])

  const getEditingStyle = useCallback((): TextRunStyleUpdate | null => {
    // Read-and-clear: reported on the one style read that follows the keypress, then gone — never
    // left to resurface on some later, unrelated read of the editing style.
    const boundaryHit = pointSizeBoundaryHitRef.current

    pointSizeBoundaryHitRef.current = false
    const withBoundaryFlag = (result: TextRunStyleUpdate | null): TextRunStyleUpdate | null =>
      boundaryHit ? { ...(result ?? {}), pointSizeClampedAtBoundary: true } : result

    const pending = pendingRunStyleRef.current
    // Read caret/selection live from the DOM to avoid React state batching lag.
    const liveCaretIndex = textareaRef.current?.selectionStart ?? 0
    const liveSelectionEnd = textareaRef.current?.selectionEnd ?? liveCaretIndex

    // A real (non-collapsed) selection may span runs with different tracking/leading — compute
    // Mixed across the whole range instead of reading a single run, mirroring the text-box engine's
    // selectionStyleFromRanges. Kerning is intentionally excluded: it has no meaning over a range.
    if (liveSelectionEnd > liveCaretIndex) {
      if (pending) return withBoundaryFlag(pending)

      return withBoundaryFlag(
        selectionMixedStyle(
          charRunMap,
          liveCaretIndex,
          liveSelectionEnd,
          paragraphs,
          carets,
          consumedLength
        )
      )
    }

    let run: TextRun | null = null

    for (let i = Math.max(0, liveCaretIndex - 1); i >= 0; i--) {
      if (charRunMap[i] != null) {
        run = charRunMap[i]
        break
      }
    }

    if (!run) {
      for (let i = liveCaretIndex; i < charRunMap.length; i++) {
        if (charRunMap[i] != null) {
          run = charRunMap[i]
          break
        }
      }
    }

    const style: TextRunStyleUpdate = {}

    // Alignment: find the paragraph containing the caret. Paragraph-level, like dropCapSummary
    // below — resolved regardless of whether a run was found above, since a collapsed caret is
    // always inside exactly one paragraph even when that paragraph is empty (e.g. a freshly
    // created text box, or the caret sitting in a blank spacer paragraph). Without this, entering
    // edit on an empty paragraph returned `null` outright (see the deleted `if (!run) return`
    // above), leaving the Type panel's Drop Cap section showing whatever the PREVIOUSLY edited
    // paragraph had instead of this paragraph's own (possibly NONE) state.
    let paraOffset = 0
    let isInEmptyParagraph = false

    for (const para of paragraphs) {
      const paraLen = (para.textLines ?? [])
        .flatMap((l) => l.textRuns ?? [])
        .reduce((sum, r) => sum + (r.textContent ?? '').length, 0)

      if (liveCaretIndex <= paraOffset + paraLen) {
        if (typeof para.alignment === 'number') style.alignment = para.alignment
        // A collapsed caret is always in exactly one paragraph, so this is a summary of that one
        // paragraph alone — never mixed — but going through the same function TypePanel already
        // reads for both other cases keeps all three (collapsed caret, range selection, whole-
        // frame selection) resolving drop cap identically.
        style.dropCapSummary = summarizeDropCapSelection([para])

        // No character anywhere (a brand-new frame): read the paragraph's own empty seed run, which
        // carries the font, face and size the first typed character will get. Without it the Type
        // panel's Style field showed its "Typeface style" placeholder until the user typed
        // (WMPCM-12385).
        //
        // An empty paragraph further down is the same case: the scan above walks back across the
        // paragraph breaks and finds the line ABOVE, but the first character typed here takes this
        // paragraph's own run, so that is the one to report (WMPCM-11367).
        const ownRun = para.textLines?.[0]?.textRuns?.[0] ?? null

        run = paraLen === 0 && ownRun ? ownRun : (run ?? ownRun)
        isInEmptyParagraph = paraLen === 0
        break
      }

      paraOffset += paraLen + 1
    }

    if (run) {
      if (typeof run.fontFamilyId === 'number') style.fontFamilyId = run.fontFamilyId
      if (typeof run.fontId === 'number') style.fontId = run.fontId
      if (typeof run.pointSize === 'number') style.pointSize = run.pointSize
      if (typeof run.leading === 'string') style.leading = run.leading
      if (typeof run.kerning === 'number') style.kerning = run.kerning
      if (typeof run.tracking === 'number') style.tracking = run.tracking
      if (typeof run.underline === 'boolean') style.underline = run.underline
      if (typeof run.strikethrough === 'boolean') style.strikethrough = run.strikethrough
    }

    // Leading is a row property, so the caret reads its whole row, as a selection does: a caret in
    // 7pt text beside 24pt text on one Metric row shows the row's 28.8, not 8.4. An empty paragraph
    // has no row of its own and keeps its seed run's value.
    if (!isInEmptyParagraph) {
      const row = caretRowRange(carets, charRunMap, liveCaretIndex, consumedLength)
      const rowLeading = row
        ? summarizeRowsLeading([
            charRunMap
              .slice(row.start, row.end)
              .filter((rowRun): rowRun is TextRun => rowRun != null),
          ])
        : null

      if (rowLeading) {
        if (rowLeading.leading !== undefined) style.leading = rowLeading.leading
        // A size chosen at the caret is typed onto this row, so it counts too.
        style.leadingPointSize = Math.max(
          rowLeading.pointSize,
          typeof pending?.pointSize === 'number' ? pending.pointSize : 0
        )
      }
    }

    // A pending style (e.g. a font size chosen at the caret, waiting for the next typed character)
    // sits on top of the caret run's real style, not in place of it. Returning only the pending
    // fields dropped the text's own Leading, so after a font-size change the panel fell back to
    // Metric and showed 38.4 for a typed 30 (AC6, WMPCM-9899).
    const merged = pending ? { ...style, ...pending } : style

    return withBoundaryFlag(Object.keys(merged).length > 0 ? merged : null)
  }, [carets, charRunMap, consumedLength, paragraphs, pendingRunStyleRef, textareaRef])

  /**
   * Kerning is caret-only: applied immediately to the single character before the caret, never
   * deferred as a pending style (unlike other run fields at a collapsed caret) and never applied
   * across a selection. The textarea's `value` string is unchanged by this (only run metadata
   * changes), so the browser preserves `selectionStart`/`selectionEnd` automatically — no manual
   * caret restore needed here, unlike the contenteditable text-box engine.
   */
  const applyKerningAtCaret = useCallback(
    (value: number): boolean => {
      const ta = textareaRef.current

      if (!ta) return false

      const selStart = ta.selectionStart ?? 0
      const selEnd = ta.selectionEnd ?? selStart

      if (selStart !== selEnd) return false

      const kerningState = getKerningStateAtFlatCaret(paragraphs, selStart, true)

      if (!kerningState.available) return false

      const updatedParagraphs = applyStyleToExactCharRange(
        paragraphs,
        { kerning: value },
        selStart - 1,
        selStart
      )

      onParagraphsChange(shape.id, updatedParagraphs)

      return true
    },
    [onParagraphsChange, paragraphs, shape.id, textareaRef]
  )

  const getKerningState = useCallback((): KerningCaretState => {
    const ta = textareaRef.current
    const selStart = ta?.selectionStart ?? 0
    const selEnd = ta?.selectionEnd ?? selStart

    return getKerningStateAtFlatCaret(paragraphs, selStart, selStart === selEnd)
  }, [paragraphs, textareaRef])

  return {
    applyRunStyle,
    getEditingStyle,
    applyKerningAtCaret,
    getKerningState,
    markPointSizeBoundaryHit,
  }
}
